import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  ListOrderedIcon,
  MegaphoneIcon,
  MonitorIcon,
  TrophyIcon,
  UsersIcon,
} from 'lucide-react';
import {
  DrawRevealKind,
  DrawStatus,
  progressiveShortlistPayloadSchema,
  type DrawRevealStep,
  type ProgressiveShortlistPayload,
} from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/page-header';
import { StatCard } from '@/components/stat-card';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice, WarningNotice } from '@/components/states';
import {
  useAdminDraw,
  useAnnounceWinners,
  useBroadcastOverlayAccess,
  useRevealStep,
} from '@/lib/admin-queries';
import { overlayPresence, presenceAgo, type OverlayPresence } from '@/lib/overlay-presence';
import { formatTashkent, formatTashkentTime } from '@/lib/time';
import { strings } from '@/i18n/strings';

const PRESENCE_TICK_MS = 1_000;

/**
 * Soniya sayin yuradigan soat — belgining O'Z ritmi.
 *
 * Hukmni o'zgartiradigan narsa serverdan keladigan `overlayAgeMs` (u o'sib boradi, ya'ni javob
 * ham o'zgaradi va React Query komponentni qayta chizadi). Bu taymer esa sanoqni SO'ROVLAR
 * ORASIDA yurgizadi: `refetchInterval` fokusdan chiqqan oynada to'xtaydi — operator OBS ga o'tsa
 * aynan shunday bo'ladi — va sanoq o'sha lahzada qotib qolardi. Belgi so'rovning ritmiga
 * bog'lanmasligi kerak.
 */
function useNowMs(intervalMs: number = PRESENCE_TICK_MS): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);

  return now;
}

/** OBS manbasi tirikligining bitta qatorlik xulosasi. */
function OverlayPresenceBadge({ presence }: { presence: OverlayPresence }) {
  if (presence.state === 'never') {
    return <Badge variant="outline">{strings.reveal.overlayNeverSeen}</Badge>;
  }

  const { value, unit } = presenceAgo(presence.agoMs);
  const ago = `${value} ${unit === 'seconds' ? strings.reveal.overlaySecondsAgo : strings.reveal.overlayMinutesAgo}`;

  return (
    <Badge
      variant={presence.state === 'live' ? 'success' : 'destructive'}
      // Muzlab qolgan manba jim turadi — shuning uchun o'zgarish e'lon qilinadi, aks holda uni
      // faqat ekranga qarab turgan odam sezadi.
      role={presence.state === 'stale' ? 'alert' : undefined}
    >
      {presence.state === 'live' ? <CheckIcon aria-hidden="true" /> : null}
      {presence.state === 'live' ? strings.reveal.overlayLive : strings.reveal.overlayStale}
      {/*
       * Sanoq soniyada bir marta o'zgaradi, e'lon esa `role="alert"` ostida turibdi — ochiq
       * qoldirilsa ekran o'quvchi butun jumlani soniya sayin qayta o'qib berardi va operator
       * signalni o'chirib qo'yardi. E'lonning MA'NOSI yuqoridagi barqaror yozuvda.
       */}
      <span aria-hidden="true">{` · ${ago}`}</span>
    </Badge>
  );
}

const STEP_LABEL: Record<string, string> = {
  [DrawRevealKind.TICKET_COUNT]: strings.reveal.revealTicketCount,
  [DrawRevealKind.SHORTLIST]: strings.reveal.revealShortlist,
  [DrawRevealKind.WINNER]: strings.reveal.revealWinner,
};

const STATUS_LABEL: Record<string, string> = {
  [DrawStatus.SCHEDULED]: strings.draws.statusScheduled,
  [DrawStatus.COMPLETED]: strings.draws.statusCompleted,
  [DrawStatus.POSTPONED]: strings.draws.statusPostponed,
};

/**
 * Mirrors the server's 1..N chain rule so one shaped historical row cannot deadlock the console.
 *
 * `afterSeq` — joriy g'olibning zanjiri qayerdan boshlanadi. Bir nechta g'olibli efirda oldingi
 * g'olibning ochilgan harflari yangisiga qo'shilib ketmasligi kerak: server ham xuddi shu
 * chegaradan foydalanadi (`DrawRevealService.latestProgressiveShortlist`).
 */
function latestProgressiveReveal(
  steps: DrawRevealStep[],
  winningCode: string | null,
  afterSeq = 0,
): ProgressiveShortlistPayload | null {
  let progression: ProgressiveShortlistPayload | null = null;
  for (const step of [...steps].sort((left, right) => left.seq - right.seq)) {
    if (step?.kind !== DrawRevealKind.SHORTLIST || step.seq <= afterSeq) continue;

    const parsed = progressiveShortlistPayloadSchema.safeParse(step.payload);
    if (!parsed.success) continue;

    const payload = parsed.data;
    const expectedCharacters = (progression?.revealedCharacters ?? 0) + 1;
    const expectedPrefix = winningCode?.slice(0, 'QT-'.length + expectedCharacters);
    if (
      payload.revealedCharacters !== expectedCharacters ||
      (progression && payload.totalCharacters !== progression.totalCharacters) ||
      (winningCode &&
        (payload.totalCharacters !== winningCode.length - 'QT-'.length ||
          payload.codePrefix !== expectedPrefix))
    ) {
      progression = null;
      continue;
    }

    progression = payload;
  }

  return progression;
}

/**
 * Boshlovchining konsoli (implementation-plan §3.6.2).
 *
 * Bitta qoida butun ekranni belgilaydi: **konsol serverdagi holatni ko'rsatadi, o'zining
 * taxminini emas.** Har bir e'londan keyin tortishuv qayta o'qiladi, `revealedKinds` esa
 * tugmalarni o'chiradi — chunki `ticket_count` va `winner` bir martalik (bazada qisman unique
 * indeks bilan), va efir o'rtasida "bosildimi?" degan savol eng qimmat savol.
 */
export function RevealConsolePage() {
  const { drawId } = useParams({ from: '/shell/draws/$drawId' });
  const draw = useAdminDraw(drawId);
  const reveal = useRevealStep(drawId);
  const overlayAccess = useBroadcastOverlayAccess(drawId);
  const [overlayUrl, setOverlayUrl] = useState<string | null>(null);
  const [overlayCopyStatus, setOverlayCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  // G'olib e'loni — konsoldagi YAGONA qaytarib bo'lmaydigan qadam, shuning uchun u tasdiqdan
  // o'tadi. Holat sahifa darajasida turadi: oyna yopilishi POST yuborilgandan keyin bo'ladi.
  const [confirmingWinner, setConfirmingWinner] = useState(false);
  // Butun bazaga ketadigan e'lon — konsoldagi IKKINCHI qaytarib bo'lmaydigan qadam, ya'ni u ham
  // o'z tasdig'i bilan.
  const announce = useAnnounceWinners(drawId);
  const [confirmingAnnounce, setConfirmingAnnounce] = useState(false);
  const nowMs = useNowMs();

  const generateOverlayUrl = async () => {
    setOverlayUrl(null);
    setOverlayCopyStatus('idle');
    try {
      const access = await overlayAccess.mutateAsync();
      const url = `${window.location.origin}/overlay/${encodeURIComponent(
        drawId,
      )}#token=${encodeURIComponent(access.accessToken)}`;
      setOverlayUrl(url);
    } catch {
      // The mutation error is rendered below; the scoped credential is never copied to logs.
    }
  };

  const copyOverlayUrl = async () => {
    if (!overlayUrl) return;
    try {
      await navigator.clipboard.writeText(overlayUrl);
      setOverlayCopyStatus('copied');
    } catch {
      setOverlayCopyStatus('error');
    }
  };

  if (draw.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (draw.isError || !draw.data) {
    return <ErrorNotice error={draw.error} />;
  }

  const detail = draw.data;
  /*
   * Serverdagi yosh + javob kelganidan beri o'tgan vaqt.
   *
   * Ikkinchi qo'shiluvchi AYNI BIR soatning ikki o'qishi orasidagi farq (`dataUpdatedAt` ni ham
   * `nowMs` ni ham brauzer qo'yadi), birinchisi esa serverning o'z ayirmasi — ya'ni hukmga hech
   * qayerda ikki xil soat solishtirilmaydi. Shu qo'shiluvchi tufayli belgi so'rovlar orasida ham
   * o'sib boradi, va konsol serverga umuman yeta olmay qolganda ham (javob eskiraveradi) manba
   * to'g'ri ravishda «javob bermayapti» ga o'tadi.
   */
  const presence = overlayPresence(
    detail.overlayAgeMs === null
      ? null
      : detail.overlayAgeMs + Math.max(0, nowMs - draw.dataUpdatedAt),
  );
  const revealed = new Set(detail.revealedKinds);
  const period = detail.drawDate ?? detail.monthKey ?? '';
  const ticketCountRevealed = revealed.has(DrawRevealKind.TICKET_COUNT);

  /**
   * Marosim bir nechta g'olib uchun TAKRORLANADI, shuning uchun konsol ikkita savolga javob
   * berishi kerak: kim navbatda va hammasi tugadimi. Ikkalasi ham e'lon qilingan `winner`
   * bosqichlaridan hisoblanadi — server ham aynan shundan hisoblaydi.
   */
  const winnerSteps = detail.steps
    .filter((step) => step.kind === DrawRevealKind.WINNER)
    .sort((left, right) => left.seq - right.seq);
  const announcedCodes = winnerSteps
    .map((step) => (step.payload as { ticketCode?: unknown }).ticketCode)
    .filter((code): code is string => typeof code === 'string');
  const nextWinner = detail.winners.find((winner) => !announcedCodes.includes(winner.ticketCode));
  /**
   * Ikkita alohida savol, ikkita alohida javob.
   *
   * `anyWinnerRevealed` — marosim ishtirokchilar sonidan o'tib ketganmi (u bir martalik fakt,
   * serverning `revealedKinds` xulosasi yetarli). `allWinnersAnnounced` — hamma g'olib
   * aytilib bo'ldimi, ya'ni bosqich tugmalari o'chadimi. Bir nechta g'olibli efirda ikkinchisi
   * birinchisidan ANCHA keyin rost bo'ladi.
   */
  const anyWinnerRevealed = revealed.has(DrawRevealKind.WINNER);
  const allWinnersAnnounced = !nextWinner;
  const progressive = latestProgressiveReveal(
    detail.steps,
    nextWinner?.ticketCode ?? null,
    winnerSteps.at(-1)?.seq ?? 0,
  );
  const progressiveComplete =
    progressive !== null && progressive.revealedCharacters >= progressive.totalCharacters;
  const maskedCode = progressive
    ? `${progressive.codePrefix}${'•'.repeat(
        Math.max(0, progressive.totalCharacters - progressive.revealedCharacters),
      )}`
    : null;

  const send = (kind: DrawRevealKind, payload?: Record<string, unknown>) =>
    reveal.mutate({ kind, payload, expectedLastSeq: detail.lastSeq });

  // Tasdiq bosilgandan keyingina g'olib serverga ketadi — tugmaning o'zi faqat oynani ochadi.
  const confirmWinner = () => {
    setConfirmingWinner(false);
    send(DrawRevealKind.WINNER);
  };

  const confirmAnnounce = () => {
    setConfirmingAnnounce(false);
    announce.mutate();
  };

  /**
   * E'lon tugmasining holati SERVERDAN keladi (`winnersAnnouncedAt`), mutatsiyaning
   * `isSuccess` idan emas: sahifa yangilangach mahalliy holat yo'qoladi va tugma yana ochiq
   * ko'rinardi — operator uni ikkinchi marta bosardi.
   */
  const winnersAnnounced = detail.winnersAnnouncedAt !== null;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title={`${strings.reveal.title} — ${period}`}
        description={strings.reveal.subtitle}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/">
              <ArrowLeftIcon aria-hidden="true" />
              {strings.nav.draws}
            </Link>
          </Button>
        }
      />

      {detail.status === DrawStatus.SCHEDULED ? (
        <WarningNotice>{strings.reveal.notCompleted}</WarningNotice>
      ) : null}
      {detail.status === DrawStatus.POSTPONED ? (
        <WarningNotice>{strings.reveal.postponed}</WarningNotice>
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="bg-muted grid size-9 shrink-0 place-items-center rounded-md">
              <MonitorIcon className="size-4" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-sm font-semibold">{strings.reveal.overlayHeading}</h2>
              {overlayUrl ? (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {strings.reveal.overlayReady}
                </p>
              ) : null}
              {overlayAccess.isError ? (
                <p className="text-destructive mt-0.5 text-xs" role="alert">
                  {strings.reveal.overlayAccessFailed}
                </p>
              ) : null}
              {overlayCopyStatus === 'error' ? (
                <p className="text-destructive mt-0.5 text-xs" role="alert">
                  {strings.reveal.overlayCopyFailed}
                </p>
              ) : null}
              <div className="mt-1.5">
                <OverlayPresenceBadge presence={presence} />
              </div>
              {/* Belgi bilan izoh bitta hukmdan chiziladi — ular hech qachon bir-biriga zid bo'lolmaydi. */}
              {presence.state === 'stale' ? (
                <p className="text-muted-foreground mt-1 text-xs">
                  {strings.reveal.overlayStaleHint}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={overlayUrl ? 'outline' : 'default'}
              size="sm"
              loading={overlayAccess.isPending}
              onClick={() => void generateOverlayUrl()}
            >
              <MonitorIcon aria-hidden="true" />
              {overlayAccess.isPending
                ? strings.reveal.generatingOverlayAccess
                : strings.reveal.generateOverlayAccess}
            </Button>
            {overlayUrl ? (
              <>
                <Button variant="secondary" size="sm" onClick={() => void copyOverlayUrl()}>
                  {overlayCopyStatus === 'copied' ? (
                    <CheckIcon aria-hidden="true" />
                  ) : (
                    <CopyIcon aria-hidden="true" />
                  )}
                  {overlayCopyStatus === 'copied'
                    ? strings.reveal.overlayUrlCopied
                    : strings.reveal.copyOverlayUrl}
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={overlayUrl} target="_blank" rel="noreferrer">
                    <ExternalLinkIcon aria-hidden="true" />
                    {strings.reveal.openOverlay}
                  </a>
                </Button>
              </>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={strings.draws.columnStatus}
          value={
            <StatusBadge status={detail.status} className="text-sm">
              {STATUS_LABEL[detail.status]}
            </StatusBadge>
          }
        />
        <StatCard
          label={strings.reveal.ticketCount}
          value={detail.ticketCount ?? strings.draws.noWinner}
          icon={UsersIcon}
        />
        <StatCard label={strings.reveal.prize} value={detail.prizeLabel} icon={TrophyIcon} />
        <StatCard
          label={strings.reveal.stepsCount}
          value={detail.steps.length}
          icon={ListOrderedIcon}
          hint={`${strings.reveal.scheduledAt}: ${formatTashkent(detail.scheduledAt)}`}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>{strings.reveal.title}</CardTitle>
            <CardDescription>{strings.reveal.offsetNote}</CardDescription>
          </CardHeader>

          <CardContent className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => send(DrawRevealKind.TICKET_COUNT)}
                disabled={ticketCountRevealed || anyWinnerRevealed || reveal.isPending}
              >
                <UsersIcon aria-hidden="true" />
                {strings.reveal.revealTicketCount}
              </Button>
              {ticketCountRevealed ? (
                <Badge variant="success">
                  <CheckIcon aria-hidden="true" />
                  {strings.reveal.alreadyRevealed}
                </Badge>
              ) : null}
            </div>

            <Separator />

            <div className="flex flex-col gap-4">
              <div>
                <h3 className="text-sm font-semibold">{strings.reveal.progressiveHeading}</h3>
                <p className="text-muted-foreground mt-1 text-sm">
                  {strings.reveal.progressiveHint}
                </p>
              </div>

              {progressive && maskedCode ? (
                <div className="bg-muted/50 flex flex-col gap-3 rounded-md px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-muted-foreground text-xs font-medium uppercase">
                        {strings.reveal.maskedTicket}
                      </div>
                      <div className="mt-1 font-mono text-2xl font-semibold tracking-normal">
                        {maskedCode}
                      </div>
                    </div>
                    <Badge variant={progressiveComplete ? 'success' : 'secondary'}>
                      {progressiveComplete
                        ? strings.reveal.progressiveComplete
                        : strings.reveal.progressiveInProgress}
                    </Badge>
                  </div>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-muted-foreground">{strings.reveal.characterProgress}</dt>
                      <dd className="tabular mt-0.5 font-medium">
                        {progressive.revealedCharacters} / {progressive.totalCharacters}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{strings.reveal.candidateCount}</dt>
                      <dd className="tabular mt-0.5 font-medium">
                        {progressive.candidateCount.toLocaleString('uz-UZ')}
                      </dd>
                    </div>
                  </dl>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">{strings.reveal.progressiveWaiting}</p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => send(DrawRevealKind.SHORTLIST)}
                  disabled={
                    !ticketCountRevealed ||
                    progressiveComplete ||
                    allWinnersAnnounced ||
                    reveal.isPending
                  }
                >
                  <ChevronRightIcon aria-hidden="true" />
                  {strings.reveal.revealNextCharacter}
                </Button>
                {!ticketCountRevealed ? (
                  <span className="text-muted-foreground text-xs">
                    {strings.reveal.ticketCountFirst}
                  </span>
                ) : null}
              </div>
            </div>

            <Separator />

            {/*
              G'olib tugmasi bosilishi bilan e'lon minglab ekranga chiqadi va uni ortga qaytarish
              yo'q — shuning uchun bosish ikkiga bo'lingan: tugma faqat oynani ochadi, `POST` esa
              oynadagi tasdiqdan keyin ketadi. Oynada bilet kodi turadi, ya'ni operator kimni
              e'lon qilayotganini KO'RIB turib tasdiqlaydi.

              Boshqa bosqichlar (ishtirokchilar soni, keyingi belgi) ataylab tasdiqsiz: ular efir
              ritmini tutib turadi va qaytarib bo'ladigan darajada arzon — har bosishda oyna
              ochilsa, marosim uzilib qolardi.
            */}
            <div className="flex flex-wrap items-center gap-2">
              <Dialog open={confirmingWinner} onOpenChange={setConfirmingWinner}>
                <DialogTrigger asChild>
                  <Button
                    disabled={allWinnersAnnounced || !progressiveComplete || reveal.isPending}
                  >
                    <TrophyIcon aria-hidden="true" />
                    {reveal.isPending ? strings.reveal.revealing : strings.reveal.revealWinner}
                  </Button>
                </DialogTrigger>

                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{strings.reveal.confirmWinnerTitle}</DialogTitle>
                    <DialogDescription>{strings.reveal.confirmWinnerBody}</DialogDescription>
                  </DialogHeader>

                  {/*
                   * Oynada AYNAN keyingi e'lon qilinadigan g'olib turadi. Bir nechta g'olibli
                   * efirda tugma ketma-ket bosiladi va ular bir-biriga o'xshaydi — operator
                   * kimni chaqirayotganini ko'rib turmasa, ikkinchi bosishda adashish oson.
                   */}
                  {nextWinner ? (
                    <div className="bg-muted/50 rounded-md px-4 py-3">
                      <div className="text-muted-foreground text-xs font-medium uppercase">
                        {strings.reveal.winnerTicket}
                      </div>
                      <div className="mt-1 font-mono text-2xl font-semibold">
                        {nextWinner.ticketCode}
                      </div>
                      <div className="text-muted-foreground mt-1 text-sm">
                        {nextWinner.userDisplayName}
                      </div>
                    </div>
                  ) : null}

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">{strings.common.cancel}</Button>
                    </DialogClose>
                    <Button onClick={confirmWinner}>
                      <TrophyIcon aria-hidden="true" />
                      {strings.reveal.confirmWinnerAction}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              {allWinnersAnnounced ? (
                <Badge variant="success">
                  <CheckIcon aria-hidden="true" />
                  {strings.reveal.alreadyRevealed}
                </Badge>
              ) : null}
              {!allWinnersAnnounced && !progressiveComplete ? (
                <span className="text-muted-foreground text-xs">
                  {strings.reveal.completeRevealFirst}
                </span>
              ) : null}
            </div>

            {reveal.isError ? <ErrorNotice error={reveal.error} /> : null}

            <Separator />

            {/*
              G'oliblarni butun bazaga e'lon qilish — efirning OXIRGI qadami.
              Tugma marosim tugamaguncha ochilmaydi (`allWinnersAnnounced`): xabar odam ekranda
              ko'rmagan natijani aytib qo'yishi mumkin bo'lgan yagona joy shu.
            */}
            <div className="flex flex-col gap-3">
              <div>
                <h3 className="text-sm font-medium">{strings.reveal.announceHeading}</h3>
                <p className="text-muted-foreground mt-1 text-xs">{strings.reveal.announceHint}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Dialog open={confirmingAnnounce} onOpenChange={setConfirmingAnnounce}>
                  <DialogTrigger asChild>
                    <Button
                      variant="secondary"
                      disabled={
                        !allWinnersAnnounced ||
                        winnersAnnounced ||
                        !detail.liveEnabled ||
                        announce.isPending
                      }
                    >
                      <MegaphoneIcon aria-hidden="true" />
                      {announce.isPending
                        ? strings.reveal.announcing
                        : strings.reveal.announceAction}
                    </Button>
                  </DialogTrigger>

                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{strings.reveal.confirmAnnounceTitle}</DialogTitle>
                      <DialogDescription>{strings.reveal.confirmAnnounceBody}</DialogDescription>
                    </DialogHeader>

                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">{strings.common.cancel}</Button>
                      </DialogClose>
                      <Button onClick={confirmAnnounce}>
                        <MegaphoneIcon aria-hidden="true" />
                        {strings.reveal.confirmAnnounceAction}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {winnersAnnounced ? (
                  <Badge variant="success">
                    <CheckIcon aria-hidden="true" />
                    {strings.reveal.announceDone}
                  </Badge>
                ) : null}
                {!allWinnersAnnounced ? (
                  <span className="text-muted-foreground text-xs">
                    {strings.reveal.announceRevealFirst}
                  </span>
                ) : null}
                {allWinnersAnnounced && !detail.liveEnabled ? (
                  <span className="text-muted-foreground text-xs">
                    {strings.reveal.announceLiveDisabled}
                  </span>
                ) : null}
              </div>

              {/*
                Navbat Telegram tezligiga yoyiladi, ya'ni "yuborildi" bilan "hamma oldi" bir xil
                payt emas — operator ikkala raqamni ham ko'rishi kerak.
              */}
              {announce.isSuccess ? (
                <p className="text-muted-foreground text-xs" role="status">
                  {strings.reveal.announceQueued.replace(
                    '{count}',
                    announce.data.recipients.toLocaleString('uz-UZ'),
                  )}{' '}
                  {announce.data.lastScheduledFor
                    ? strings.reveal.announceFinishesAt.replace(
                        '{time}',
                        formatTashkentTime(announce.data.lastScheduledFor),
                      )
                    : ''}
                </p>
              ) : null}

              {announce.isError ? <ErrorNotice error={announce.error} /> : null}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          {/*
            G'olib efirdan OLDIN ham shu yerda ko'rinadi: tortishuv soatlar oldin o'tkaziladi va
            boshlovchi kimni e'lon qilishini bilishi kerak. Foydalanuvchi tomonidagi qat'iy spoiler
            qoidasi (`DrawListingService`) aynan shu sababdan admin yuzasiga tarqatilmagan.
          */}
          {detail.winners.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>{strings.reveal.winnerHeading}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {detail.winners.map((winner, index) => {
                  const announced = announcedCodes.includes(winner.ticketCode);
                  const isNext = nextWinner?.ticketCode === winner.ticketCode;
                  return (
                    <div
                      key={winner.ticketCode}
                      className={isNext ? 'border-primary rounded-md border p-3' : 'p-3'}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-muted-foreground text-xs tracking-wide uppercase">
                          {strings.reveal.winnerRank.replace('{rank}', String(index + 1))}
                        </span>
                        <Badge variant={announced ? 'success' : isNext ? 'secondary' : 'outline'}>
                          {announced
                            ? strings.reveal.winnerAnnounced
                            : isNext
                              ? strings.reveal.winnerNext
                              : strings.reveal.winnerWaiting}
                        </Badge>
                      </div>
                      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
                        <dt className="text-muted-foreground">{strings.reveal.winnerTicket}</dt>
                        <dd className="font-mono font-medium">{winner.ticketCode}</dd>
                        <dt className="text-muted-foreground">{strings.reveal.winnerUser}</dt>
                        <dd className="font-medium">
                          {winner.userDisplayName}
                          {winner.username ? ` (@${winner.username})` : ''}
                        </dd>
                        <dt className="text-muted-foreground">{strings.reveal.winnerTelegram}</dt>
                        <dd className="tabular font-mono">{winner.telegramId}</dd>
                        {/*
                          Faqat o'ziga alohida sovrin biriktirilgan bo'lsa. `null` — efirda
                          bitta sovrin bo'lgan, va u sarlavhadagi umumiy qatorda turibdi;
                          uni bu yerda takrorlash operatorga har bir g'olib alohida sovrin
                          olgandek ko'rinardi.
                        */}
                        {winner.prizeLabel !== null && (
                          <>
                            <dt className="text-muted-foreground">
                              {strings.draws.winnerPrizeColumn}
                            </dt>
                            <dd className="font-medium">{winner.prizeLabel}</dd>
                          </>
                        )}
                      </dl>
                    </div>
                  );
                })}

                {detail.videoEmbedUrl ? (
                  <a
                    href={detail.videoEmbedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary mt-4 inline-flex items-center gap-1.5 text-sm hover:underline"
                  >
                    <ExternalLinkIcon className="size-3.5" aria-hidden="true" />
                    {strings.reveal.videoLink}
                  </a>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>{strings.reveal.stepsHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.steps.length === 0 ? (
                <EmptyState title={strings.reveal.noSteps} icon={ListOrderedIcon} />
              ) : (
                <ol className="flex flex-col gap-2">
                  {detail.steps.map((step) => (
                    <li
                      key={step.seq}
                      className="bg-muted/40 flex items-center justify-between gap-4 rounded-md px-3 py-2 text-sm"
                    >
                      <span className="font-medium">{STEP_LABEL[step.kind] ?? step.kind}</span>
                      <span className="tabular text-muted-foreground text-xs">
                        {formatTashkent(step.revealedAt)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
